import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Card, 
  Button, 
  Row,
  Col,
  Typography,
  Spin,
  message,
  Tag,
  Divider,
  Space,
  List,
  Avatar,
  Alert,
  Collapse
} from 'antd';
import { 
  ArrowLeftOutlined,
  LinkOutlined,
  UserOutlined,
  ClockCircleOutlined,
  MailOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axios from '../../utils/axios';
import { useAuthCheck } from '../../hooks/useAuth';
import AgentPromptButton from '../ai/AgentPromptButton';
import './analysis.css';
import { issue_explanations } from './AnalysisUtils';

const { Title, Text, Paragraph } = Typography;

const InsightDetailPage = () => {
  useAuthCheck();
  
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [loading, setLoading] = useState(true);
  const [insight, setInsight] = useState(null);
  const [contactsLoading, setContactsLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsError, setContactsError] = useState(null);
  
  const contactsRef = useRef(null);
  const timelineRef = useRef(null);

  const scrollToContacts = () => {
    contactsRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToTimeline = () => {
    timelineRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const jobId = searchParams.get('jobId');
  const insightId = searchParams.get('insightId');

  const issue_names = {
    'cta_conflict': 'Conflicting CTAs',
    'frequency_issue': 'Same Tool Overlap',
    'communication_overlap': 'Cross-Tool Overlap',
    'mrr_attribution': 'Campaign Attribution',
    'mrr_attribution_cta': 'CTA Attribution',
    'mrr_read_attribution': 'Campaign Attribution',
    'mrr_read_attribution_cta': 'CTA Attribution',
  };

  const event_source_names = {
    'hubspot_webhook_events': { name: 'HubSpot', style: 'hubspot' },
    'esputnik_webhook_events': { name: 'eSputnik', style: 'esputnik' },
    'stripe_webhook_events': { name: 'Stripe', style: 'stripe' },
  };

  useEffect(() => {
    if (jobId && insightId) {
      loadInsight();
    } else {
      message.error('Missing job ID or insight ID');
      navigate('/analysis');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, insightId]);

  const loadInsight = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/analyze/aggregation/jobs/${jobId}/insights/${insightId}`);
      setInsight(response.data);
      
      // Load contact details after insight is loaded
      if (response.data.contact_ids && response.data.contact_ids.length > 0) {
        fetchContactDetails(response.data.contact_ids);
      }
    } catch (error) {
      message.error('Failed to load insight details');
      console.error('Error loading insight:', error);
      navigate('/analysis');
    } finally {
      setLoading(false);
    }
  };

  const fetchContactDetails = async (contactIds) => {
    setContactsLoading(true);
    setContactsError(null);
    try {
      // Use the batch contact details API for better performance
      const response = await axios.post('/timeline/contacts/batch-details', contactIds);
      
      if (response.data.success) {
        const contactDetails = response.data.contacts.map(contact => ({
          id: contact.id,
          email: contact.email,
          firstName: contact.first_name,
          lastName: contact.last_name,
          phone: contact.phone,
          hasData: true
        }));
        setContacts(contactDetails);
      } else {
        throw new Error('Failed to fetch contact details');
      }
    } catch (error) {
      console.error('Error fetching contact details:', error);
      // Fallback to showing contact IDs only
      setContacts(contactIds.map(id => ({
        id: id,
        email: null,
        firstName: null,
        lastName: null,
        hasData: false
      })));
    } finally {
      setContactsLoading(false);
    }
  };

  const formatContactName = (contact) => {
    // Prefer first_name + last_name if available
    if (contact.firstName || contact.lastName) {
      const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ');
      if (name.trim()) {
        return name;
      }
    }
    
    // Fallback to extracting name from email
    if (contact.email) {
      const emailParts = contact.email.split('@');
      const localPart = emailParts[0];
      // Try to format name from email local part
      const formattedName = localPart
        .replace(/[._-]/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
      return formattedName;
    }
    
    // Final fallback
    return contact.name || `Contact ${contact.id}`;
  };

  const formatContactSubtitle = (contact) => {
    if (contact.email) {
      return contact.email;
    }
    return `ID: ${contact.id}`;
  };

  const calculateTimeGap = (startTimestamp, triggerTimestamp) => {
    if (!startTimestamp || !triggerTimestamp) return 'N/A';
    
    const start = moment(startTimestamp);
    const trigger = moment(triggerTimestamp);
    const duration = moment.duration(trigger.diff(start));
    
    const days = Math.floor(duration.asDays());
    const hours = duration.hours();
    const minutes = duration.minutes();
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const getSourceInfo = (eventSource) => {
    return event_source_names[eventSource] || { name: eventSource, style: 'default' };
  };

  const renderEventCard = (event, title, isFirst = false) => {
    if (!event) return null;

    const sourceInfo = getSourceInfo(event.event_source);
    
    return (
      <div 
        style={{ 
          padding: '12px 16px',
          borderLeft: `3px solid ${isFirst ? '#1890ff' : '#52c41a'}`,
          backgroundColor: '#fafafa',
          borderRadius: '0 4px 4px 0'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, gap: 12 }}>
          <MailOutlined style={{ color: isFirst ? '#1890ff' : '#52c41a' }} />
          <Text strong style={{ fontSize: 13 }}>{title}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {moment(event.timestamp).format('MMM DD, HH:mm')}
          </Text>
          <Tag className={`esp-tag esp-tag-${sourceInfo.style}`} style={{ margin: 0 }}>
            {sourceInfo.name}
          </Tag>
        </div>
        
        <Text style={{ display: 'block', marginBottom: 6 }}>
          {event.subject || event.description || 'N/A'}
        </Text>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {event.content_group_link ? (
            <a 
              href={event.content_group_link} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#1890ff', fontSize: 12 }}
            >
              <LinkOutlined style={{ marginRight: 4 }} />
              {event.content_group_name}
            </a>
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {event.content_group_name || 'N/A'}
            </Text>
          )}
          <Tag color="blue" style={{ margin: 0, fontSize: 10 }}>{event.content_group_type}</Tag>
          {event.cta && (
            <Tag color="purple" style={{ margin: 0, fontSize: 10 }}>{event.cta}</Tag>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="analysis-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!insight) {
    return (
      <div className="analysis-page">
        <Card>
          <Text>Insight not found</Text>
        </Card>
      </div>
    );
  }

  const explanation = issue_explanations[insight.insight_type];
  const timeGap = calculateTimeGap(
    insight.events_data?.event_start?.timestamp,
    insight.events_data?.event_trigger?.timestamp
  );

  return (
    <div className="analysis-page">
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <Button 
          type="text" 
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(`/analysis?jobId=${jobId}`)}
          style={{ marginBottom: 16 }}
        >
          Back to Analysis
        </Button>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Space align="center" style={{ marginBottom: 8 }}>
              <Title level={2} style={{ marginBottom: 0 }}>
                {issue_names[insight.insight_type] || insight.insight_type}
              </Title>
              <AgentPromptButton feature="intelligence" context={{ job_id: jobId, insight_id: insightId }} />
            </Space>
            <Space size="middle">
              <Tag
                icon={<UserOutlined />}
                style={{ fontSize: 14, padding: '4px 12px', cursor: 'pointer' }}
                onClick={scrollToContacts}
              >
                {insight.affected_contacts} Contacts Affected
              </Tag>
              <Tag
                icon={<ClockCircleOutlined />}
                style={{ fontSize: 14, padding: '4px 12px', cursor: 'pointer' }}
                onClick={scrollToTimeline}
              >
                Gap: {timeGap}
              </Tag>
            </Space>
          </div>
        </div>
      </div>

      <Row gutter={[24, 24]}>
        {/* Main Content */}
        <Col xs={24} lg={16}>
        {/* Why It Matters */}
        {explanation && (
            <Collapse
              style={{ marginBottom: 24 }}
              items={[
                {
                  key: '1',
                  label: (
                    <div>
                      <Text strong style={{ fontSize: 14 }}>Why It Matters</Text>
                      <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: 4, fontSize: 13 }}>
                        {explanation.description}
                      </Paragraph>
                    </div>
                  ),
                  children: (
                    <div>
                      <Text strong style={{ display: 'block', marginBottom: 8 }}>Impact:</Text>
                      <List
                        size="small"
                        dataSource={explanation.impacts}
                        renderItem={item => (
                          <List.Item style={{ padding: '4px 0', border: 'none' }}>
                            <Text type="secondary" style={{ fontSize: 13 }}>• {item}</Text>
                          </List.Item>
                        )}
                      />
                      
                      <Divider style={{ margin: '16px 0' }} />
                      
                      <Paragraph type="secondary" style={{ fontStyle: 'italic', marginBottom: 0 }}>
                        {explanation.recommendation}
                      </Paragraph>
                    </div>
                  ),
                }
              ]}
            />
          )}
          {/* Event Details */}
          <Card 
            ref={timelineRef}
            title="Event Timeline"
            style={{ marginBottom: 24 }}
            bodyStyle={{ padding: '16px' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {renderEventCard(insight.events_data?.event_trigger, 'Trigger Email', true)}
              
              <div style={{ 
                textAlign: 'left', 
                padding: '4px 0 4px 16px',
                color: '#8c8c8c',
                fontSize: 12
              }}>
                <ClockCircleOutlined style={{ marginRight: 6 }} />
                <span>Gap: {timeGap}</span>
              </div>
              
              {renderEventCard(insight.events_data?.event_start, 'First Email')}
            </div>
          </Card>

          {/* Affected Contacts */}
          <Card 
            ref={contactsRef}
            title={
              <Space>
                <UserOutlined />
                <span>Affected Contacts ({insight.affected_contacts})</span>
              </Space>
            }
          >
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              {insight.affected_contacts} contacts received both emails within the overlap window.
            </Paragraph>
            
            {contactsLoading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <Spin size="default" />
                <div style={{ marginTop: '12px' }}>
                  <Text type="secondary">Loading contact details...</Text>
                </div>
              </div>
            ) : contactsError ? (
              <Alert message={contactsError} type="error" />
            ) : contacts.length === 0 ? (
              <Text type="secondary">No contacts found</Text>
            ) : (
              <List
                dataSource={contacts}
                style={{ width: '100%', textAlign: 'left' }}
                renderItem={(contact, index) => (
                  <List.Item
                    key={contact.id}
                    style={{ 
                      borderBottom: index === contacts.length - 1 ? 'none' : '1px solid #f0f0f0',
                      padding: '12px 0',
                      width: '100%',
                      justifyContent: 'flex-start'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', justifyContent: 'flex-start' }}>
                      <Avatar 
                        icon={<UserOutlined />} 
                        style={{ 
                          backgroundColor: contact.hasData ? '#1890ff' : '#d9d9d9',
                          color: 'white',
                          flexShrink: 0
                        }}
                      />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <a 
                          href={`/timeline-list?contact=${encodeURIComponent(contact.id.toString())}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ 
                            color: '#1890ff',
                            textDecoration: 'none',
                            fontWeight: 500,
                            display: 'block'
                          }}
                          onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                          onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                        >
                          {formatContactName(contact)}
                        </a>
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {formatContactSubtitle(contact)}
                        </Text>
                      </div>
                    </div>
                  </List.Item>
                )}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default InsightDetailPage;

